// @ai-generated 2026-08-24T12:24:35+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { type ApiContainerAction, EnumContainerOrderAction } from '@Model';
import {
    type AsyncStateUpdateRequest,
    type Awaitable,
    FlexColumn,
    FlexRow,
    useTranslate,
} from '@synqo/frontend-component-library';
import { hasRemarks } from './actionDetails';
import styles from './ContainerActions.module.css';
import { ContainerActionQuantityCell } from './ContainerActionQuantityCell';
import { type PlannedContainerGroup } from './containerActionGroups';
import { type ContainerOrderState, isContentWeighed } from './containerRowState';
import { ContainerActionLine, ContainerLineFields } from './ContainerLineLayout';
import { ContainerLineConfirmation } from './ContainerLineConfirmation';
import { ContainerMaterialCell } from './ContainerMaterialCell';
import { ContainerTypeCell } from './ContainerTypeCell';
import { type ContainerLineState, lineState } from './lineConfirmation';
import { type ContainerMaterial } from './useContainerMaterial';
import { type ContainerTypeChange } from './useContainerTypeChange';

interface CollectiveContainerRowProps {
    group: PlannedContainerGroup;
    /** Path to the order; the row's own segment is appended here. */
    baseApiObjectPath: string;
    canSetMaterial: boolean;
    /** Whether rows may be removed; the stepper's minus deletes them. */
    canDelete: boolean;
    /** Whether the user may confirm lines and take confirmations back. */
    canMarkForBooking: boolean;
    isMarking: boolean;
    completing: boolean;
    focused?: boolean;
    material: ContainerMaterial;
    orderState: ContainerOrderState;
    /** How far the group may grow; unset where nothing caps it. */
    standingCount?: number;
    typeChange: ContainerTypeChange;
    requestUpdateAction: (updater: AsyncStateUpdateRequest<ApiContainerAction>, actionId: string) => Awaitable<boolean>;
    onDelete: (actionIds: string[]) => void;
    onGrow?: (group: PlannedContainerGroup, count: number) => void;
    growLocked?: boolean;
    onMarkGroupForBooking: (group: PlannedContainerGroup) => void;
    onUnmarkGroupForBooking: (group: PlannedContainerGroup) => void;
}

/**
 * One collective type as one line: counted rather than tracked per container, so it carries a count
 * and never a number, and it is confirmed in one go.
 *
 * The count is the number of rows behind the line, so the stepper deletes rows rather than writing a
 * number anywhere.
 */
export function CollectiveContainerRow({
    group,
    baseApiObjectPath,
    canSetMaterial,
    canDelete,
    canMarkForBooking,
    isMarking,
    completing,
    focused = false,
    material,
    orderState,
    standingCount,
    typeChange,
    requestUpdateAction,
    onDelete,
    onGrow,
    growLocked,
    onMarkGroupForBooking,
    onUnmarkGroupForBooking,
}: CollectiveContainerRowProps) {
    const i18n = useTranslate('');

    const countOf = (state: ContainerLineState) =>
        group.actions.filter((action) => lineState(action.booking_status) === state).length;
    const isClosed = countOf('confirmed') > 0;
    const [anchor, ...others] = group.actions.filter(
        (action) =>
            lineState(action.booking_status) === 'open' ||
            (orderState.correcting && lineState(action.booking_status) === 'booked'),
    );
    const openMaterial = focused && !isClosed && group.actions.every((action) => !action.content.foreign_key);
    const entryOf = (state: ContainerLineState) =>
        countOf(state) > 0 && (
            <ContainerLineConfirmation
                action={group.action}
                state={state}
                completing={completing}
                canConfirm={canMarkForBooking}
                locked={isMarking}
                count={countOf(state)}
                onConfirm={() => onMarkGroupForBooking(group)}
                onUnconfirm={() => onUnmarkGroupForBooking(group)}
            />
        );

    const remarks = [...new Set(group.actions.map((action) => action.remarks).filter((text) => text !== ''))];

    const details = (
        <FlexColumn gap="xxs" align="start">
            <FlexRow className={styles.detailCaption}>{i18n('Bemerkung')}</FlexRow>
            {remarks.length === 0 ? (
                <FlexRow className={styles.detailCaption}>{i18n('Keine Bemerkung')}</FlexRow>
            ) : (
                remarks.map((text) => (
                    <FlexRow key={text} className={styles.remarkText}>
                        {text}
                    </FlexRow>
                ))
            )}
        </FlexColumn>
    );

    const status = (
        <>
            {entryOf('booked')}
            {entryOf('confirmed')}
            {entryOf('open')}
        </>
    );

    return (
        <ContainerActionLine
            action={group.action}
            removeLabel={i18n('Container entfernen')}
            invalid={
                group.actions.some((action) => orderState.foreignToSite.has(action.id))
                    ? i18n('Steht nicht am gewählten Standort.')
                    : undefined
            }
            onRemove={
                canDelete && !isClosed && group.removableActions.length > 0
                    ? () => onDelete(group.removableActions.map((action) => action.id))
                    : undefined
            }
            details={details}
            detailsLabel="Bemerkung"
            hasDetails={group.actions.some(hasRemarks)}
        >
            <ContainerLineFields
                type={
                    canSetMaterial && !isClosed && anchor && group.action === EnumContainerOrderAction.Place ? (
                        <ContainerTypeCell
                            action={anchor}
                            baseApiObjectPath={`${baseApiObjectPath}/container_actions`}
                            canEdit
                            correcting={orderState.correcting}
                            requestUpdateState={(updater) => requestUpdateAction(updater, anchor.id)}
                            otherActionIds={others.map((action) => action.id)}
                            typeChange={typeChange}
                        />
                    ) : (
                        <ContainerTypeCell
                            action={group.actions[0]}
                            baseApiObjectPath={`${baseApiObjectPath}/container_actions`}
                            canEdit={false}
                            requestUpdateState={(updater) => requestUpdateAction(updater, group.actions[0].id)}
                        />
                    )
                }
                material={
                    <ContainerMaterialCell
                        key={openMaterial ? 'open' : 'closed'}
                        autoOpen={openMaterial}
                        actions={group.actions}
                        baseApiObjectPath={`${baseApiObjectPath}/container_actions`}
                        correcting={orderState.correcting}
                        canEdit={
                            canSetMaterial &&
                            !isClosed &&
                            !group.actions.some((action) => isContentWeighed(action, orderState.positions))
                        }
                        material={material}
                        requestUpdateAction={requestUpdateAction}
                    />
                }
                detail={
                    <ContainerActionQuantityCell
                        group={group}
                        canDelete={canDelete && !isClosed}
                        onDelete={onDelete}
                        onGrow={isClosed ? undefined : onGrow}
                        growLocked={growLocked}
                        max={standingCount}
                    />
                }
                status={status}
            />
        </ContainerActionLine>
    );
}
